// @spec GHG-STR-108
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2, Loader2, Play, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ReportingPeriod } from '@/features/datahub/periods/types';
import {
  useActivityAssignmentJob,
  useActivityAssignmentQueue,
  useCreateActivityAssignmentJob,
  useFinalizeActivityAssignmentQueueItem,
} from '@/features/ghg/api/use-activity-assignment';
import {
  useGHGSettings,
  useUpdateGHGSettings,
} from '@/features/ghg/api/use-ghg-settings';
import { ActivityAssignmentCandidateCard } from '@/features/ghg/components/ActivityAssignmentCandidateCard';
import { ActivityAssignmentFactorOverrideDialog } from '@/features/ghg/components/ActivityAssignmentFactorOverrideDialog';
import { ActivityAssignmentFactorOverrideSummary } from '@/features/ghg/components/ActivityAssignmentFactorOverrideSummary';
import { useGHGRole } from '@/features/ghg/hooks/useGHGRole';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import type { EmissionFactorSearchContext } from '@/features/ghg/hooks/useEmissionFactorSearchState';
import type { EmissionFactor } from '@/features/ghg/types';
import type {
  ActivityAssignmentQueueItem,
  AssignmentStatus,
  FactorStatus,
} from '@/features/ghg/types/activity-assignment';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

function getScopeOptions(t: (key: string) => string): SelectOption[] {
  return [
    { value: 'all', label: t('assignmentQueue.allScopes') },
    { value: '1', label: t('assignmentQueue.scope1') },
    { value: '2', label: t('assignmentQueue.scope2') },
    { value: '3', label: t('assignmentQueue.scope3') },
  ];
}

function getFactorStatusOptions(t: (key: string) => string): SelectOption[] {
  return [
    { value: 'all', label: t('assignmentQueue.allFactorStatuses') },
    { value: 'unresolved', label: t('assignmentQueue.unresolved') },
    { value: 'manual_required', label: t('assignmentQueue.manualRequired') },
    { value: 'resolved', label: t('assignmentQueue.resolved') },
  ];
}

function getAssignmentStatusLabels(t: (key: string) => string): Record<AssignmentStatus, string> {
  return {
    pending: t('assignmentQueue.statusPending'),
    suggested: t('assignmentQueue.statusSuggested'),
    needs_review: t('assignmentQueue.statusNeedsReview'),
    confirmed: t('assignmentQueue.statusConfirmed'),
    failed: t('assignmentQueue.statusFailed'),
    completed: t('assignmentQueue.statusCompleted'),
  };
}

const ASSIGNMENT_STATUS_VARIANTS: Record<
  AssignmentStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  suggested: 'outline',
  needs_review: 'outline',
  confirmed: 'default',
  failed: 'destructive',
  completed: 'default',
};

function getActiveJobStatusLabel(status: 'pending' | 'processing', t: (key: string) => string) {
  return status === 'processing' ? t('assignmentQueue.processing') : t('assignmentQueue.queued');
}

type FactorOverrideState =
  | { kind: 'none' }
  | {
      kind: 'factor';
      emissionFactorId: string;
      factorPreview: EmissionFactor;
      selectorSource: 'climatiq' | 'custom';
    }
  | { kind: 'direct'; manualFactorKgCo2ePerUnit: string };

const EMPTY_OVERRIDE: FactorOverrideState = { kind: 'none' };

function getReportingYear(item: ActivityAssignmentQueueItem, periods: ReportingPeriod[]): number | undefined {
  const matchedPeriod = periods.find((candidate) => candidate.id === item.reportingPeriodId);
  if (!matchedPeriod?.startDate) return undefined;
  const year = new Date(matchedPeriod.startDate).getFullYear();
  return Number.isFinite(year) ? year : undefined;
}

function buildFactorSearchContext(
  item: ActivityAssignmentQueueItem,
  periods: ReportingPeriod[]
): EmissionFactorSearchContext {
  const activityLabel =
    item.suggestedAssignment?.displayName.trim() ??
    item.autopilotSuggestion?.emissionFactor.name.trim();
  const descriptionQuery = item.activityDescription.trim();

  return {
    scope: item.scope,
    ...(item.suggestedAssignment?.activityId.trim()
      ? { activityId: item.suggestedAssignment.activityId.trim() }
      : {}),
    ...(activityLabel ? { activityLabel } : {}),
    ...(descriptionQuery
      ? { query: descriptionQuery }
      : activityLabel
        ? { query: activityLabel }
        : {}),
    ...(getReportingYear(item, periods) ? { year: getReportingYear(item, periods) } : {}),
  };
}

export const Route = createFileRoute('/_authenticated/ghg/activity-data/assignment-queue')({
  validateSearch: (search: Record<string, unknown>) => ({
    jobId: typeof search.jobId === 'string' ? search.jobId : undefined,
  }),
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.factorReview'),
  }),
  component: ActivityAssignmentQueuePage,
});

export function ActivityAssignmentQueuePage() {
  const { t } = useTranslation('ghg');
  const scopeOptions = useMemo(() => getScopeOptions(t), [t]);
  const factorStatusOptions = useMemo(() => getFactorStatusOptions(t), [t]);
  const assignmentStatusLabels = useMemo(() => getAssignmentStatusLabels(t), [t]);
  const navigate = useNavigate();
  const { jobId: searchJobId } = useSearch({
    from: '/_authenticated/ghg/activity-data/assignment-queue',
  });
  const { periodId, period, periods } = useGHGPagePeriod();
  const { isAdmin } = useGHGRole();
  const { settings } = useGHGSettings();
  const updateSettingsMutation = useUpdateGHGSettings();

  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [factorStatusFilter, setFactorStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [jobId, setJobId] = useState<string | null>(searchJobId ?? null);
  const [overrideDialogItemId, setOverrideDialogItemId] = useState<string | null>(null);
  const [selectedQueueItemIds, setSelectedQueueItemIds] = useState<string[]>([]);
  const [factorOverrides, setFactorOverrides] = useState<Record<string, FactorOverrideState>>({});
  const [finalizingQueueItemIds, setFinalizingQueueItemIds] = useState<string[]>([]);
  const [autoApproveThresholdInput, setAutoApproveThresholdInput] = useState('60');
  const savedThreshold =
    settings?.emissionFactorPreferences.bulkImportAutoApproveConfidenceThreshold ?? 60;

  const filters = useMemo(
    () => ({
      reportingPeriodId: periodId || undefined,
      scope: scopeFilter === 'all' ? undefined : (Number(scopeFilter) as 1 | 2 | 3),
      factorStatus:
        factorStatusFilter === 'all' ? undefined : (factorStatusFilter as FactorStatus),
      page,
      pageSize: 25,
    }),
    [factorStatusFilter, page, periodId, scopeFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [scopeFilter, factorStatusFilter]);

  useEffect(() => {
    setAutoApproveThresholdInput(String(savedThreshold));
  }, [savedThreshold]);

  useEffect(() => {
    setJobId(searchJobId ?? null);
  }, [searchJobId]);

  const queue = useActivityAssignmentQueue(filters);
  const createJobMutation = useCreateActivityAssignmentJob();
  const finalizeMutation = useFinalizeActivityAssignmentQueueItem();
  const jobQuery = useActivityAssignmentJob(jobId, {
    enabled: !!jobId,
    refetchIntervalMs: 2000,
  });

  const selectableItemIds = useMemo(
    () =>
      queue.items
        .filter((item) => item.assignmentStatus !== 'completed' && !item.isProcessing)
        .map((item) => item.id),
    [queue.items]
  );
  const isAllPageSelected =
    selectableItemIds.length > 0 && selectableItemIds.every((id) => selectedQueueItemIds.includes(id));
  const isSomePageSelected =
    !isAllPageSelected && selectableItemIds.some((id) => selectedQueueItemIds.includes(id));

  useEffect(() => {
    setSelectedQueueItemIds((prev) => {
      const next = prev.filter((id) => selectableItemIds.includes(id));
      return next.length === prev.length && next.every((id, index) => id === prev[index])
        ? prev
        : next;
    });
  }, [selectableItemIds]);

  useEffect(() => {
    setFactorOverrides((prev) => {
      const visibleIds = new Set(queue.items.map((item) => item.id));
      const nextEntries = Object.entries(prev).filter(([itemId]) => visibleIds.has(itemId));
      return nextEntries.length === Object.keys(prev).length ? prev : Object.fromEntries(nextEntries);
    });
  }, [queue.items]);

  const activeOverrideItem =
    overrideDialogItemId != null
      ? queue.items.find((item) => item.id === overrideDialogItemId) ?? null
      : null;
  const activeOverrideContext = useMemo(
    () =>
      activeOverrideItem ? buildFactorSearchContext(activeOverrideItem, periods) : { scope: 1 as const },
    [activeOverrideItem, periods]
  );
  const activeOverride =
    activeOverrideItem != null ? factorOverrides[activeOverrideItem.id] ?? EMPTY_OVERRIDE : EMPTY_OVERRIDE;

  const handleRunJob = async () => {
    if (selectedQueueItemIds.length === 0) {
      toast.error(t('ghg:toast.selectAtLeastOneQueueRowToRunAssignment'));
      return;
    }

    try {
      const response = await createJobMutation.mutateAsync({
        queueItemIds: selectedQueueItemIds,
        batchSize: selectedQueueItemIds.length,
      });
      setJobId(response.id);
      void navigate({
        to: '/ghg/activity-data/assignment-queue',
        search: { jobId: response.id },
        replace: true,
      });
      setSelectedQueueItemIds([]);
      toast.success(t('ghg:toast.assignmentJobStarted'));
      await queue.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('ghg:toast.failedToStartAssignmentJob'));
    }
  };

  const handleFinalizeRow = async (item: ActivityAssignmentQueueItem) => {
    const selectedCatalogEntryId = item.suggestedAssignment?.catalogEntryId;
    const override = factorOverrides[item.id] ?? EMPTY_OVERRIDE;
    const canFinalizeWithoutSuggestedAssignment =
      (item.assignmentStatus === 'failed' && override.kind !== 'none') ||
      Boolean(item.autopilotSuggestion);

    if (!selectedCatalogEntryId && !canFinalizeWithoutSuggestedAssignment) {
      toast.error(t('ghg:toast.noSuggestedAssignmentAvailableForThisRowYet'));
      return;
    }

    setFinalizingQueueItemIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));

    try {
      await finalizeMutation.mutateAsync({
        itemId: item.id,
        data: {
          ...(selectedCatalogEntryId ? { selectedCatalogEntryId } : {}),
          ...(override.kind === 'factor'
            ? { emissionFactorId: override.emissionFactorId }
            : {}),
          ...(override.kind === 'direct'
            ? {
                manualFactorKgCo2ePerUnit: Number(override.manualFactorKgCo2ePerUnit),
              }
            : {}),
        },
      });
      toast.success(t('ghg:toast.queueRowFinalizedAndActivityEntryCreated'));
      setFactorOverrides((prev) => {
        const { [item.id]: _removed, ...rest } = prev;
        return rest;
      });
      await queue.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('ghg:toast.failedToFinalizeQueueRow'));
    } finally {
      setFinalizingQueueItemIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const handleDirectOverrideSelect = (itemId: string, value: string) => {
    setFactorOverrides((prev) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [itemId]: {
          kind: 'direct',
          manualFactorKgCo2ePerUnit: trimmedValue,
        },
      };
    });
  };

  const handleFactorOverrideSelect = (itemId: string, factorId: string, factor?: EmissionFactor) => {
    if (!factor) {
      toast.error(t('ghg:toast.selectedFactorPreviewIsUnavailableTryAgain'));
      return;
    }

    setFactorOverrides((prev) => ({
      ...prev,
      [itemId]: {
        kind: 'factor',
        emissionFactorId: factorId,
        factorPreview: factor,
        selectorSource: factor.selectorSource ?? 'climatiq',
      },
    }));
  };

  const clearFactorOverride = (itemId: string) => {
    setFactorOverrides((prev) => {
      const { [itemId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const activeJob = jobQuery.data;
  const hasPeriods = periods.length > 0;
  const parsedAutoApproveThreshold = Number(autoApproveThresholdInput);
  const isAutoApproveThresholdValid =
    Number.isFinite(parsedAutoApproveThreshold) &&
    parsedAutoApproveThreshold >= 0 &&
    parsedAutoApproveThreshold <= 100;
  const isAutoApproveThresholdDirty =
    isAutoApproveThresholdValid && parsedAutoApproveThreshold !== savedThreshold;

  const handleSaveAutoApproveThreshold = async () => {
    if (!isAutoApproveThresholdValid) {
      toast.error(t('assignmentQueue.toastThresholdRange'));
      return;
    }

    try {
      await updateSettingsMutation.mutateAsync({
        emissionFactorPreferences: {
          bulkImportAutoApproveConfidenceThreshold: parsedAutoApproveThreshold,
        },
      });
      toast.success(t('assignmentQueue.toastThresholdUpdated'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('assignmentQueue.toastThresholdUpdateFailed')
      );
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">{t('assignmentQueue.heading')}</h1>
              <p className="mt-1 text-muted-foreground">{t('assignmentQueue.description')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({ to: '/ghg/activity-data/import' });
                }}
              >
                {t('assignmentQueue.bulkImport')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({ to: '/ghg/collect' });
                }}
              >
                {t('assignmentQueue.activityData')}
              </Button>
            </div>
          </div>
          <Tabs
            value="review"
            onValueChange={(value) => {
              if (value !== 'review') {
                void navigate({
                  to: '/ghg/emission-factors',
                  search: { tab: value as 'library' | 'ai-mappings' | 'custom' | 'collections' },
                });
              }
            }}
          >
            <TabsList>
              <TabsTrigger value="library">{t('factorLibraryPage.tabLibrary')}</TabsTrigger>
              <TabsTrigger value="ai-mappings">{t('factorLibraryPage.tabAIMappings')}</TabsTrigger>
              <TabsTrigger value="custom">{t('factorLibraryPage.tabCustomFactors')}</TabsTrigger>
              <TabsTrigger value="collections">{t('factorLibraryPage.tabCollections')}</TabsTrigger>
              <TabsTrigger value="review">{t('factorLibraryPage.tabFactorReview')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </header>

        <div className="rounded-lg border bg-muted/10 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('assignmentQueue.autoApproveThreshold')}</p>
              <p className="text-sm text-muted-foreground">
                {t('assignmentQueue.autoApproveThresholdDesc')}
              </p>
            </div>
            {isAdmin ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="space-y-1">
                  <label
                    className="text-xs font-medium text-muted-foreground"
                    htmlFor="bulk-import-auto-approve-threshold"
                  >
                    {t('assignmentQueue.confidencePercent')}
                  </label>
                  <Input
                    id="bulk-import-auto-approve-threshold"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={autoApproveThresholdInput}
                    onChange={(event) => {
                      setAutoApproveThresholdInput(event.target.value);
                    }}
                    className="w-32"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    void handleSaveAutoApproveThreshold();
                  }}
                  disabled={
                    updateSettingsMutation.isPending ||
                    !isAutoApproveThresholdValid ||
                    !isAutoApproveThresholdDirty
                  }
                >
                  {updateSettingsMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {t('assignmentQueue.saveThreshold')}
                </Button>
              </div>
            ) : (
              <Badge variant="outline">{savedThreshold}%</Badge>
            )}
          </div>
          {!isAdmin ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('assignmentQueue.thresholdManagedByAdmins')}
            </p>
          ) : null}
          {isAdmin && !isAutoApproveThresholdValid ? (
            <p className="mt-2 text-xs text-destructive">{t('assignmentQueue.enterValueRange')}</p>
          ) : null}
        </div>

        {!hasPeriods ? (
          <Alert>
            <AlertDescription>
              {t('assignmentQueue.noReportingPeriods')}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-3 rounded-lg border bg-muted/10 p-4 sm:grid-cols-2 xl:grid-cols-3">
          <div className="min-w-0 space-y-2">
            <p className="text-sm font-medium">{t('assignmentQueue.scopeFilter')}</p>
            <Select options={scopeOptions} value={scopeFilter} onChange={setScopeFilter} />
          </div>
          <div className="min-w-0 space-y-2">
            <p className="text-sm font-medium">{t('assignmentQueue.factorStatusFilter')}</p>
            <Select
              options={factorStatusOptions}
              value={factorStatusFilter}
              onChange={setFactorStatusFilter}
            />
          </div>
          <div className="min-w-0 sm:col-span-2 xl:col-span-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end xl:justify-end">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto sm:max-w-full"
              onClick={() => {
                void queue.refetch();
              }}
              disabled={queue.isLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('assignmentQueue.refresh')}
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto sm:max-w-full"
              onClick={() => {
                void handleRunJob();
              }}
              disabled={createJobMutation.isPending || selectedQueueItemIds.length === 0}
            >
              {createJobMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {t('assignmentQueue.runReviewJob', { count: selectedQueueItemIds.length })}
            </Button>
            </div>
          </div>
        </div>

        {activeJob ? (
          <Alert>
            <AlertDescription>
              {t('assignmentQueue.jobStatus', {
                id: activeJob.id,
                status: activeJob.status,
                processed: activeJob.processedItems,
                total: activeJob.totalItems,
                needsReview: activeJob.needsReviewItems,
                autoApproved: activeJob.autoApprovedItems,
                failed: activeJob.failedItems,
              })}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={isAllPageSelected || (isSomePageSelected ? 'indeterminate' : false)}
                    onCheckedChange={(value) => {
                      if (value) {
                        setSelectedQueueItemIds((prev) => [...new Set([...prev, ...selectableItemIds])]);
                        return;
                      }
                      setSelectedQueueItemIds((prev) =>
                        prev.filter((id) => !selectableItemIds.includes(id))
                      );
                    }}
                    aria-label={t('assignmentQueue.selectAllRows')}
                  />
                </TableHead>
                <TableHead>{t('assignmentQueue.thDescription')}</TableHead>
                <TableHead className="w-24">{t('assignmentQueue.thScope')}</TableHead>
                <TableHead className="w-[30rem]">{t('assignmentQueue.thSuggestedMatch')}</TableHead>
                <TableHead className="w-36">{t('assignmentQueue.thStatus')}</TableHead>
                <TableHead className="w-48">{t('assignmentQueue.thAction')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                    {t('assignmentQueue.loading')}
                  </TableCell>
                </TableRow>
              ) : queue.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {t('assignmentQueue.noItems')}
                  </TableCell>
                </TableRow>
              ) : (
                queue.items.map((item) => {
                  const override = factorOverrides[item.id] ?? EMPTY_OVERRIDE;
                  const hasAutopilotSuggestion = Boolean(item.autopilotSuggestion);
                  const isFinalizingRow = finalizingQueueItemIds.includes(item.id);
                  const isRowProcessing = item.isProcessing;
                  const isRowLocked = isFinalizingRow || item.assignmentStatus === 'completed' || isRowProcessing;
                  const canOverrideFactor =
                    !isRowLocked &&
                    (Boolean(item.suggestedAssignment) ||
                      hasAutopilotSuggestion ||
                      item.assignmentStatus === 'failed');
                  const canConfirmRow =
                    !isRowLocked &&
                    (Boolean(item.suggestedAssignment) ||
                      hasAutopilotSuggestion ||
                      (item.assignmentStatus === 'failed' && override.kind !== 'none'));
                  const shouldExplainManualFactor =
                    item.assignmentStatus === 'failed' &&
                    !item.suggestedAssignment &&
                    !hasAutopilotSuggestion;

                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedQueueItemIds.includes(item.id)}
                          disabled={item.assignmentStatus === 'completed' || isRowProcessing}
                          onCheckedChange={(value) => {
                            setSelectedQueueItemIds((prev) => {
                              if (value) {
                                return prev.includes(item.id) ? prev : [...prev, item.id];
                              }
                              return prev.filter((id) => id !== item.id);
                            });
                          }}
                          aria-label={t('assignmentQueue.selectQueueRow', { description: item.activityDescription })}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{item.activityDescription}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.activityCategory} · {item.activityMethod} · {item.quantity} {item.unit}
                        </p>
                        {item.failureReason ? (
                          <p className="mt-1 text-xs text-amber-700">{item.failureReason}</p>
                        ) : null}
                      </TableCell>
                      <TableCell>{item.scope}</TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          {item.suggestedAssignment ? (
                            <ActivityAssignmentCandidateCard
                              candidate={item.suggestedAssignment}
                              showActivityId={false}
                            />
                          ) : item.autopilotSuggestion ? (
                            <div className="rounded-md border bg-muted/10 p-3">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0 flex-1 space-y-1">
                                  <p className="text-sm font-medium">
                                    {item.autopilotSuggestion.emissionFactor.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {item.autopilotSuggestion.emissionFactor.source} ·{' '}
                                    {item.autopilotSuggestion.emissionFactor.regionName ??
                                      item.autopilotSuggestion.emissionFactor.region}{' '}
                                    · {String(item.autopilotSuggestion.emissionFactor.year)} ·{' '}
                                    {item.autopilotSuggestion.emissionFactor.unitType}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    item.autopilotSuggestion.label === 'accept'
                                      ? 'default'
                                      : 'outline'
                                  }
                                >
                                  {item.autopilotSuggestion.label === 'accept'
                                    ? t('assignmentQueue.autopilotAccept')
                                    : t('assignmentQueue.autopilotReview')}
                                </Badge>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">{t('assignmentQueue.noAssignmentAvailable')}</p>
                          )}
                          {shouldExplainManualFactor ? (
                            <p className="text-xs text-amber-700">
                              {t('assignmentQueue.automaticAssignmentFailed')}
                            </p>
                          ) : null}
                          {override.kind !== 'none' ? (
                            <ActivityAssignmentFactorOverrideSummary
                              {...override}
                              onClear={() => {
                                clearFactorOverride(item.id);
                              }}
                              clearLabel={
                                item.suggestedAssignment || hasAutopilotSuggestion
                                  ? t('assignmentQueue.useSuggestedFactor')
                                  : t('assignmentQueue.clearOverride')
                              }
                              disabled={isRowLocked}
                            />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          {item.activeAssignmentJobStatus === 'pending' ||
                          item.activeAssignmentJobStatus === 'processing' ? (
                            <Badge variant="secondary" className="gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {getActiveJobStatusLabel(item.activeAssignmentJobStatus, t)}
                            </Badge>
                          ) : null}
                          <Badge variant={ASSIGNMENT_STATUS_VARIANTS[item.assignmentStatus]}>
                            {assignmentStatusLabels[item.assignmentStatus]}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setOverrideDialogItemId(item.id);
                            }}
                            disabled={!canOverrideFactor}
                          >
                            <SlidersHorizontal className="mr-2 h-4 w-4" />
                            {t('assignmentQueue.overrideEF')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              void handleFinalizeRow(item);
                            }}
                            disabled={!canConfirmRow}
                          >
                            {isFinalizingRow ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="mr-2 h-4 w-4" />
                            )}
                            {t('assignmentQueue.confirm')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('assignmentQueue.pagination', { page: queue.page, total: queue.total })}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                setPage((prev) => Math.max(1, prev - 1));
              }}
            >
              {t('assignmentQueue.previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={queue.page * queue.pageSize >= queue.total}
              onClick={() => {
                setPage((prev) => prev + 1);
              }}
            >
              {t('assignmentQueue.next')}
            </Button>
          </div>
        </div>
      </div>
      {activeOverrideItem ? (
        <ActivityAssignmentFactorOverrideDialog
          open={overrideDialogItemId === activeOverrideItem.id}
          onOpenChange={(open) => {
            if (!open) setOverrideDialogItemId(null);
          }}
          activityDescription={activeOverrideItem.activityDescription}
          searchContext={activeOverrideContext}
          searchInitialParams={{ unitType: activeOverrideItem.activityMethod }}
          initialMode={
            activeOverride.kind === 'direct' ||
            (!activeOverrideItem.suggestedAssignment && !activeOverrideItem.autopilotSuggestion)
              ? 'manual'
              : 'search'
          }
          initialManualFactorKgCo2ePerUnit={
            activeOverride.kind === 'direct' ? activeOverride.manualFactorKgCo2ePerUnit : ''
          }
          onApplyManualFactor={(manualFactorKgCo2ePerUnit) => {
            handleDirectOverrideSelect(activeOverrideItem.id, manualFactorKgCo2ePerUnit);
          }}
          onSelectFactor={(factorId, factor) => {
            handleFactorOverrideSelect(activeOverrideItem.id, factorId, factor);
          }}
        />
      ) : null}
    </div>
  );
}
